import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Clock, CheckCircle, Play, Loader2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import AnnouncementBar from '../components/layout/AnnouncementBar';
import BookingModal from '../components/booking/BookingModal';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const ChallengesPage = () => {
  const { isRTL } = useLanguage();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  useEffect(() => {
    const fetchChallenges = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${backendUrl}/api/challenges`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            setChallenges(data);
          }
        }
      } catch (err) {
        console.error('Error fetching challenges:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchChallenges();
  }, []);

  const getLocalizedField = (obj, field) => {
    if (isRTL) return obj[`${field}_ar`] || obj[field] || '';
    return obj[field] || '';
  };

  return (
    <div className="min-h-screen bg-charcoal">
      <AnnouncementBar />
      <Header onRegisterClick={() => setIsBookingOpen(true)} />

      <main className="pt-36 sm:pt-40 lg:pt-44 section-padding">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">

          {/* Page header */}
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
            <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
              {isRTL ? 'تحديات التحول' : 'Transformation Challenges'}
            </p>
            <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-cream leading-tight mb-4 sm:mb-6 px-2">
              {isRTL ? 'ابدئي رحلتكِ' : 'Begin Your Journey'}
            </h1>
            <div className="gold-divider" />
            <p className="text-cream-300 mt-6 sm:mt-8 px-4 text-sm sm:text-base">
              {isRTL
                ? 'تحديات فيديو يومية مجانية صُمِّمت لتُرشدكِ خطوة بخطوة نحو تحوّل حقيقي.'
                : 'Free daily video challenges designed to guide you step by step toward lasting transformation.'}
            </p>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 text-gold animate-spin" />
              <span className="ml-3 text-cream-300">{isRTL ? 'جاري التحميل...' : 'Loading challenges...'}</span>
            </div>
          )}

          {/* Empty state */}
          {!loading && challenges.length === 0 && (
            <div className="text-center py-20 text-cream-400 bg-charcoal-100 border border-white/10 rounded-xl">
              {isRTL ? 'لا توجد تحديات متاحة حالياً' : 'No challenges available at the moment.'}
            </div>
          )}

          {/* Challenge banner cards */}
          {!loading && challenges.map((c) => {
            const title = isRTL ? (c.title_ar || c.title_en || '') : (c.title_en || c.title_ar || '');
            const description = getLocalizedField(c, 'description');
            const subtitle = isRTL ? `تحدّي ${c.total_days} يوماً` : `${c.total_days}-Day Video Challenge`;
            const format = isRTL ? 'أونلاين · بوتيرتكِ' : 'Online · Self-Paced';
            const cta = isRTL ? 'ابدئي التحدّي' : 'Start the Challenge';

            return (
              <div
                key={c.slug}
                className="mb-10 sm:mb-16 relative overflow-hidden rounded-2xl border border-gold/25 bg-gradient-to-br from-charcoal-100 via-charcoal to-charcoal-100 shadow-[0_0_40px_rgba(243,210,34,0.07)]"
              >
                {/* Decorative glows */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-gold/4 rounded-full blur-2xl pointer-events-none" />

                <div className={`relative flex flex-col lg:flex-row ${isRTL ? 'lg:flex-row-reverse' : ''} items-stretch`}>
                  {/* Left: content */}
                  <div className="flex-1 p-7 sm:p-10 lg:p-12 flex flex-col justify-center">
                    {/* Badge + label */}
                    <div className="flex items-center gap-3 mb-5">
                      <span className="px-3 py-1 text-xs uppercase tracking-wider rounded-md bg-gold text-charcoal font-semibold">
                        {isRTL ? 'مجاني' : 'Free'}
                      </span>
                      <span className="text-gold/60 text-xs uppercase tracking-widest">
                        {isRTL ? 'تحدّي فيديو' : 'Video Challenge'}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight mb-2">
                      {title}
                    </h2>
                    <p className="text-gold text-sm sm:text-base italic mb-5">{subtitle}</p>

                    {/* Description */}
                    <p className="text-cream-300 text-sm sm:text-base leading-relaxed mb-6 max-w-lg">
                      {description}
                    </p>

                    {/* CTA */}
                    <div>
                      <Link
                        to={`/challenge/${c.slug}`}
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gold text-charcoal text-sm uppercase tracking-wider font-semibold hover:bg-gold/90 transition-all duration-200 active:scale-[0.98]"
                      >
                        <Play size={15} className="flex-shrink-0" />
                        <span>{cta}</span>
                      </Link>
                    </div>
                  </div>

                  {/* Right: stats panel */}
                  <div className={`lg:w-72 xl:w-80 border-t lg:border-t-0 ${isRTL ? 'lg:border-r' : 'lg:border-l'} border-gold/15 bg-gold/[0.03] flex flex-col justify-center items-center p-8 gap-6`}>
                    <div className="text-center">
                      <div className="font-serif text-6xl sm:text-7xl text-gold leading-none">
                        {c.total_days ?? '—'}
                      </div>
                      <div className="text-cream-400 text-xs uppercase tracking-[0.2em] mt-1">
                        {isRTL ? 'يوم · فيديو · تحوّل' : 'Days · Videos · Moments'}
                      </div>
                    </div>

                    <div className="w-8 h-px bg-gold/30" />

                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-xs tracking-wide">
                      <Clock size={11} />
                      {format}
                    </span>

                    <p className="text-center text-cream-400 text-xs leading-relaxed px-2">
                      {isRTL ? 'يوماً بيوم، فيديو بفيديو' : 'One day at a time, one video at a time'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <Footer />

      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        sessionType=""
        sourcePage="/challenges"
      />
    </div>
  );
};

export default ChallengesPage;
